import {createReducer} from 'redux-act';
import {
  CLEAR_ACTIVE_ORDERS,
  CLEAR_NEW_ORDERS,
  RUN_GET_ACTIVE_ORDERS_JOB,
  SET_ACTIVE_ORDERS,
  SET_NEW_ORDERS,
  CHANGE_STATUS_ACTIVE_SUCCESS,
  CHANGE_STATUS_ACTIVE,
  CHANGE_CURRENT_LOCATION_TIMEZONE,
  SET_ORDERS_SORTING,
  CHANGE_REFUNDS_ACTIVE,
  SET_ORDERS_SEARCH,
  SET_ACTIVE_EVENT_ORDERS,
  APPEND_LARGE_ORDERS,
  PREPEND_LARGE_ORDER,
  SET_LARGE_ORDER_INFO,
  FETCH_LARGE_ORDER_INFO,
  REMOVE_LARGE_ORDER,
  CLEAR_LARGE_ORDERS,
  SET_LARGE_ORDER_SUBMITTING,
  HOLD_NEW_ORDERS,
  APPEND_PENDING_NEW_ORDERS
} from 'store/global/ordersV2/action';
import {
  calculateOverdueAndFuture,
  filterEventOrders
} from 'helpers/functionUtils/ordersUtilsV2';
import {EVENT_ORDER_TYPE_SELECT, ORDER} from 'helpers/constants/orders_v2';
import {CHANGE_ORDER_INFO} from 'store/orders/orderList/action';

const initialSorting = () => ({
  sort: 'desc',
  sortBy: 'CookingEndTime.Local'
});

const initialState = () => ({
  activeOrders: undefined,
  newOrders: [],
  pendingNewOrders: [],
  largeOrders: [],
  largeOrderSubmitting: false,
  activeOrdersCount: 0,
  sorting: initialSorting(),
  changeStatusLoading: null,
  search: '',
  filteredOrders: [],
  eventOrderTypeSelected: EVENT_ORDER_TYPE_SELECT.DELIVERY
});

const activeOrdersV2Reducer = createReducer(
  {
    [SET_ACTIVE_ORDERS]: (state, payload) => {
      const orders = payload?.data.map((order) =>
        calculateOverdueAndFuture(
          {Timezone: payload?.Timezone, ...order},
          payload?.PickupReadyEnabled,
          payload?.serviceFeatures
        )
      );
      return {
        ...state,
        activeOrders: orders,
        activeOrdersCount: orders.length,
        loading: false
      };
    },
    [SET_ACTIVE_EVENT_ORDERS]: (state, payload) => {
      const orders = filterEventOrders(
        state.activeOrders,
        payload?.eventOrderTypeSelected || state.eventOrderTypeSelected
      );

      return {
        ...state,
        filteredOrders: orders,
        ...(payload?.eventOrderTypeSelected && {
          eventOrderTypeSelected: payload.eventOrderTypeSelected
        }),
        loading: false
      };
    },
    [CHANGE_STATUS_ACTIVE]: (state, payload) => ({
      ...state,
      changeStatusLoading: payload.id
    }),
    [CHANGE_STATUS_ACTIVE_SUCCESS]: (state, payload) => {
      const cloneOrders = [...state.activeOrders];
      const index = cloneOrders.findIndex(
        (order) => order[ORDER.ID] === payload[ORDER.ID]
      );

      if (index !== -1) {
        if (payload[ORDER.ORDER_STATUS]) {
          cloneOrders[index][ORDER.ORDER_STATUS] = payload[ORDER.ORDER_STATUS];
        } else {
          cloneOrders.splice(index, 1);
        }
      }

      const cloneFilteredOrders = [...state.filteredOrders];
      if (cloneFilteredOrders.length > 0) {
        const filteredIndex = cloneFilteredOrders.findIndex(
          (order) => order[ORDER.ID] === payload[ORDER.ID]
        );
        if (filteredIndex !== -1) {
          if (payload[ORDER.ORDER_STATUS]) {
            cloneFilteredOrders[filteredIndex][ORDER.ORDER_STATUS] =
              payload[ORDER.ORDER_STATUS];
          } else {
            cloneFilteredOrders.splice(filteredIndex, 1);
          }
        }
      }

      return {
        ...state,
        activeOrders: cloneOrders,
        filteredOrders: cloneFilteredOrders,
        changeStatusLoading: null
      };
    },
    [CHANGE_REFUNDS_ACTIVE]: (state, payload) => {
      const cloneOrders = [...state.activeOrders];
      const index = cloneOrders.findIndex(
        (order) => order[ORDER.ID] === payload[ORDER.ID]
      );

      if (index !== -1) {
        cloneOrders[index][ORDER.REFUND] = payload[ORDER.REFUND];
      }

      return {
        ...state,
        activeOrders: cloneOrders,
        changeStatusLoading: null
      };
    },
    [RUN_GET_ACTIVE_ORDERS_JOB]: (state) => ({
      ...state,
      loading: !state.activeOrders
    }),
    [CLEAR_ACTIVE_ORDERS]: (state) => ({
      ...state,
      activeOrders: undefined,
      activeOrdersCount: 0,
      filteredOrders: [],
      search: '',
      largeOrders: [],
      pendingNewOrders: [],
      largeOrderSubmitting: false
    }),
    [SET_NEW_ORDERS]: (state, payload) => ({
      ...state,
      newOrders: payload
    }),
    [SET_ORDERS_SORTING]: (state, payload) => {
      return {
        ...state,
        sorting: {
          ...state.sorting,
          ...payload
        }
      };
    },
    [CLEAR_NEW_ORDERS]: (state) => ({
      ...state,
      newOrders: []
    }),
    [HOLD_NEW_ORDERS]: (state) => {
      if (!state.newOrders.length) {
        return state;
      }
      const pendingIds = new Set(
        state.pendingNewOrders.map((order) => order[ORDER.ID])
      );
      const toHold = state.newOrders.filter(
        (order) => !pendingIds.has(order[ORDER.ID])
      );
      return {
        ...state,
        pendingNewOrders: [...state.pendingNewOrders, ...toHold],
        newOrders: []
      };
    },
    [APPEND_PENDING_NEW_ORDERS]: (state, payload) => {
      const existingIds = new Set(
        state.pendingNewOrders.map((order) => order[ORDER.ID])
      );
      const toAdd = (payload || []).filter(
        (order) => !existingIds.has(order[ORDER.ID])
      );
      if (!toAdd.length) {
        return state;
      }
      return {
        ...state,
        pendingNewOrders: [...state.pendingNewOrders, ...toAdd]
      };
    },
    [PREPEND_LARGE_ORDER]: (state, payload) => {
      if (!payload?.[ORDER.ID]) {
        return state;
      }
      const rest = state.largeOrders.filter(
        (order) => order[ORDER.ID] !== payload[ORDER.ID]
      );
      return {
        ...state,
        largeOrders: [{...payload, largeDeliveryLoading: true}, ...rest]
      };
    },
    [APPEND_LARGE_ORDERS]: (state, payload) => {
      const existingIds = new Set(
        state.largeOrders.map((order) => order[ORDER.ID])
      );
      const toAdd = (payload || []).filter(
        (order) => !existingIds.has(order[ORDER.ID])
      );
      if (!toAdd.length) {
        return state;
      }
      return {
        ...state,
        largeOrders: [
          ...state.largeOrders,
          ...toAdd.map((order) => ({...order, largeDeliveryLoading: true}))
        ]
      };
    },
    [FETCH_LARGE_ORDER_INFO]: (state, orderId) => ({
      ...state,
      largeOrders: state.largeOrders.map((order) =>
        order[ORDER.ID] === orderId
          ? {...order, largeDeliveryLoading: true}
          : order
      )
    }),
    [SET_LARGE_ORDER_INFO]: (state, {orderId, info}) => ({
      ...state,
      largeOrders: state.largeOrders.map((order) =>
        order[ORDER.ID] === orderId
          ? {
              ...order,
              largeDeliveryLoading: false,
              largeDelivery: {...order.largeDelivery, ...info}
            }
          : order
      )
    }),
    [REMOVE_LARGE_ORDER]: (state, orderId) => {
      const largeOrders = state.largeOrders.filter(
        (order) => order[ORDER.ID] !== orderId
      );
      if (largeOrders.length === 0 && state.pendingNewOrders.length > 0) {
        return {
          ...state,
          largeOrders,
          newOrders: state.pendingNewOrders,
          pendingNewOrders: [],
          largeOrderSubmitting: false
        };
      }
      return {
        ...state,
        largeOrders,
        largeOrderSubmitting: false
      };
    },
    [CLEAR_LARGE_ORDERS]: (state) => ({
      ...state,
      largeOrders: [],
      largeOrderSubmitting: false
    }),
    [SET_LARGE_ORDER_SUBMITTING]: (state, payload) => ({
      ...state,
      largeOrderSubmitting: Boolean(payload)
    }),
    [CHANGE_CURRENT_LOCATION_TIMEZONE]: (state, payload) => ({
      ...state,
      currentLocationTimezone: payload
    }),
    [CHANGE_ORDER_INFO]: (state, payload) => {
      const updatedOrders = state?.activeOrders?.map((order) =>
        order.id === payload.id
          ? {...order, InternalNotes: payload.note}
          : order
      );
      return {
        ...state,
        activeOrders: updatedOrders
      };
    },
    [SET_ORDERS_SEARCH]: (state, payload) => {
      let updatedOrders = state?.activeOrders;
      if (payload?.trim()) {
        updatedOrders = updatedOrders?.filter((order) =>
          order[ORDER.CUSTOMER]?.fullName
            ?.toLowerCase()
            ?.includes(payload?.trim()?.toLowerCase())
        );
      }
      return {
        ...state,
        search: payload,
        filteredOrders: updatedOrders
      };
    }
  },

  initialState()
);

export default activeOrdersV2Reducer;
